import { REVEAL_INTRO_MS, REVEAL_TALLY_MAX_MS, REVEAL_TALLY_MIN_MS, type WordView } from '../shared/results';
import { pointsForWord } from './rules';

interface ScoringPlayer {
  readonly words: readonly Readonly<WordView>[];
}

interface RecapSource {
  readonly players: readonly ScoringPlayer[];
  /** Unfound words, longest first (`RoundResults.missed`). */
  readonly missed: readonly Readonly<WordView>[];
  readonly found: number;
  readonly possible: number;
}

/** One distinct word scored in the round by any player. */
export interface RecapWord {
  readonly w: string;
  readonly pts: number;
}

/** The recap's content and timing, derived alike by room gating and every client. */
export interface RecapPlan {
  readonly words: readonly RecapWord[];
  /**
   * The board's longest word with the base points it is worth, shown after the scored words and
   * kept at standings; null when the round has no words or its missed words are unknown.
   */
  readonly longest: RecapWord | null;
  /** The scored words' share of the tally; the longest word follows for `RECAP_LONGEST_MS`. */
  readonly wordsMs: number;
  readonly tallyMs: number;
}

/** A word's place in the recap: its position in the list and 0–1 progress through its time. */
export interface RecapSlot {
  readonly index: number;
  readonly progress: number;
}

/**
 * Recap time per 100 base points, so time grows with a word's length value: a 3-letter word
 * gets 400ms, 4 letters 800ms, 5 letters 1.2s and 6 letters 2s before clamping.
 */
export const RECAP_MS_PER_HUNDRED_POINTS = 400;
/** The board's longest word closes the recap for this long, after the scored words. */
export const RECAP_LONGEST_MS = 2_000;

/** Everything the recap shows and how long it takes. */
export function recapPlan(results: RecapSource): RecapPlan {
  const words = recapWords(results.players);
  // Archives from before missed words were stored carry none while words remain unfound; the
  // found words alone cannot name the board's longest word.
  const missedUnknown = results.missed.length === 0 && results.found < results.possible;
  const longestText = missedUnknown ? null : longestWord([...words, ...results.missed]);
  const longest = longestText === null ? null : { w: longestText, pts: pointsForWord(longestText) };
  const wordsMs = recapWordsMs(words);
  return { words, longest, wordsMs, tallyMs: wordsMs + (longest === null ? 0 : RECAP_LONGEST_MS) };
}

/** Duration from revealAt until continuation; server gating and every client derive it alike. */
export function recapTotalMs(plan: RecapPlan): number {
  return REVEAL_INTRO_MS + plan.tallyMs;
}

/** Every distinct scored word, shortest first, then alphabetical so all clients share one order. */
export function recapWords(players: readonly ScoringPlayer[]): RecapWord[] {
  const byWord = new Map<string, RecapWord>();
  for (const player of players) {
    for (const word of player.words) {
      if (!byWord.has(word.w)) byWord.set(word.w, { w: word.w, pts: word.pts });
    }
  }
  return [...byWord.values()].sort((a, b) => a.w.length - b.w.length || a.w.localeCompare(b.w));
}

/**
 * The scored words' time: each word's base-point time, clamped to the tally bounds. Clamping
 * scales every word alike, so relative pacing holds in small and large rounds. A round without
 * scored words has none, so the recap goes straight to the longest word or standings.
 */
function recapWordsMs(words: readonly RecapWord[]): number {
  if (words.length === 0) return 0;
  const natural = (totalWeight(words) / 100) * RECAP_MS_PER_HUNDRED_POINTS;
  return Math.min(REVEAL_TALLY_MAX_MS, Math.max(REVEAL_TALLY_MIN_MS, natural));
}

/** The word shown at 0–1 progress through the scored words; each word's share follows its base points. */
export function recapSlot(words: readonly RecapWord[], fraction: number): RecapSlot {
  const total = totalWeight(words);
  let start = 0;
  for (const [index, word] of words.entries()) {
    const share = pointsForWord(word.w) / total;
    if (fraction < start + share || index === words.length - 1) {
      return { index, progress: Math.min(1, Math.max(0, (fraction - start) / share)) };
    }
    start += share;
  }
  throw new Error('recapSlot requires at least one word');
}

/** Longest first; equal lengths resolve alphabetically so every client names the same word. */
function longestWord(words: readonly { readonly w: string }[]): string | null {
  let longest: string | null = null;
  for (const { w } of words) {
    if (longest === null || w.length > longest.length || (w.length === longest.length && w < longest))
      longest = w;
  }
  return longest;
}

function totalWeight(words: readonly RecapWord[]): number {
  return words.reduce((sum, word) => sum + pointsForWord(word.w), 0);
}
